'use client'

import type { GraphQLResult } from 'aws-amplify/api'
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import { getSavedSearchQuery } from '@/services/graphql/savedSearch'
import { useUserContext } from '@/context/userContext.shared'
import { getAmplifyClient } from '@/services/amplifyClient'
import {
  type SavedSearchItem,
  savedSearchToAdaptedProduct,
} from '@/services/adapters/savedSearch.adapter'
import type { AdaptedProduct, Context } from '@/types'
import {
  listSavedSearchItemsWithFetcher,
  type SavedSearchFilters,
  type SavedSearchPageFetcher,
} from './savedSearch.shared'

type SavedSearchOptions = Partial<Context> & { isDone: boolean }

export type UseQuerySavedSearchesProps = {
  filters?: SavedSearchFilters
  options: SavedSearchOptions
}

export const createSavedSearchPageFetcher = (): SavedSearchPageFetcher => {
  return async ({ query, variables }) => {
    const client = getAmplifyClient()
    const { data } = (await client.graphql({
      query,
      variables,
    })) as GraphQLResult<{
      [key: string]: { items: SavedSearchItem[]; nextToken: number | null }
    }>
    const key = Object.keys(data ?? {}).find(
      (itemKey) => data?.[itemKey]?.items,
    )
    const result = key ? data?.[key] : null
    return { items: result?.items ?? [], nextToken: result?.nextToken ?? null }
  }
}

export const listSavedSearchItems = async ({
  userID,
  organizationID,
  organizationMasterID,
  isMaster,
  filters,
  fetchPage = createSavedSearchPageFetcher(),
}: {
  userID: string | undefined
  organizationID: string | undefined
  organizationMasterID: string | undefined
  isMaster: boolean
  filters?: SavedSearchFilters
  fetchPage?: SavedSearchPageFetcher
}) => {
  return listSavedSearchItemsWithFetcher({
    userID,
    organizationID,
    organizationMasterID,
    isMaster,
    filters,
    fetchPage,
  })
}

export const useQuerySavedSearches = ({
  filters,
  options,
}: UseQuerySavedSearchesProps) => {
  const { ownership, team } = useUserContext()

  const userID = ownership?.byUserID
  const organizationID = ownership?.organizationID
  const organizationMasterID = ownership?.organizationMasterID
  const isMaster = team?.isMaster ?? false

  const { data, isFetching, isError, isLoading, isSuccess, isFetched } =
    useQuery({
      queryKey: [
        'savedSearches',
        { userID, organizationID, organizationMasterID, filters, isMaster },
      ],
      queryFn: () =>
        listSavedSearchItems({
          userID,
          organizationID,
          organizationMasterID,
          isMaster,
          filters,
        }),
      enabled: !!userID && options.isDone,
    })

  const alerts = useMemo(() => {
    if (!data || !options.isDone) return undefined
    return data.map((item) => savedSearchToAdaptedProduct(item, options))
  }, [data, options])

  return {
    alerts,
    products: alerts,
    isLoading,
    isFetching,
    isSuccess,
    isFetched,
    hasNextPage: false,
    isFetchingNextPage: false,
    isError,
  }
}

export const useProjectAlertsTotal = (projectID: string | undefined) => {
  const { ownership, team } = useUserContext()

  const userID = ownership?.byUserID
  const organizationID = ownership?.organizationID
  const organizationMasterID = ownership?.organizationMasterID
  const isMaster = team?.isMaster ?? false

  return useQuery({
    queryKey: [
      'projectAlertsTotal',
      { userID, organizationID, organizationMasterID, projectID, isMaster },
    ],
    queryFn: async () => {
      const items = await listSavedSearchItems({
        userID,
        organizationID,
        organizationMasterID,
        isMaster,
        filters: projectID ? { projectID: { eq: projectID } } : undefined,
      })
      return items.length
    },
    enabled: !!userID && !!projectID && projectID !== 'create',
    placeholderData: 0,
  })
}

export const useSavedSearch = (id: string) => {
  return useQuery({
    queryKey: ['savedSearch', id],
    queryFn: async () => {
      const client = getAmplifyClient()
      const { data } = (await client.graphql({
        query: getSavedSearchQuery,
        variables: { id },
      })) as GraphQLResult<{ getSavedSearch: SavedSearchItem }>
      return data?.getSavedSearch
    },
    enabled: !!id,
    select: (item) => (item ? savedSearchToAdaptedProduct(item) : null),
  })
}

export const getSavedSearch = async (
  id: string,
): Promise<AdaptedProduct | null> => {
  try {
    const client = getAmplifyClient()
    const { data } = (await client.graphql({
      query: getSavedSearchQuery,
      variables: { id },
    })) as GraphQLResult<{ getSavedSearch: SavedSearchItem }>
    const item = data?.getSavedSearch
    return item ? savedSearchToAdaptedProduct(item) : null
  } catch (e) {
    console.error('error getting saved search', e)
    throw e
  }
}
